/**
 * Tiny keyed DOM patcher. `morphChildren(live, next)` makes the children of `live`
 * match `next` while keeping existing nodes: same-tag elements are reused (attributes and
 * children synced), so CSS transitions, focus and scroll survive a repaint. Elements that
 * carry `data-key` are matched by that key (and moved if the order changed); everything
 * else is matched by tag, in order.
 */

export interface MNode {
  nodeType: number;
  nodeName: string;
  nodeValue: string | null;
  childNodes: ArrayLike<MNode>;
  insertBefore(node: MNode, ref: MNode | null): unknown;
  removeChild(node: MNode): unknown;
  getAttribute?(name: string): string | null;
  getAttributeNames?(): string[];
  setAttribute?(name: string, value: string): void;
  removeAttribute?(name: string): void;
}

/** Return true to leave an attribute on the live element alone (client-owned state). */
export type KeepAttr = (el: MNode, attr: string) => boolean;

const ELEMENT = 1;

function keyOf(node: MNode): string | null {
  return node.nodeType === ELEMENT ? (node.getAttribute?.('data-key') ?? null) : null;
}

function sameKind(a: MNode, b: MNode): boolean {
  return a.nodeType === b.nodeType && a.nodeName === b.nodeName;
}

function morphAttrs(live: MNode, next: MNode, keep?: KeepAttr): void {
  const want = next.getAttributeNames?.() ?? [];
  for (const name of want) {
    if (keep?.(live, name)) continue;
    const value = next.getAttribute!(name) ?? '';
    if (live.getAttribute!(name) !== value) live.setAttribute!(name, value);
  }
  for (const name of live.getAttributeNames?.() ?? []) {
    if (keep?.(live, name)) continue;
    if (!want.includes(name)) live.removeAttribute!(name);
  }
}

function morphNode(live: MNode, next: MNode, keep?: KeepAttr): void {
  if (live.nodeType !== ELEMENT) {
    if (live.nodeValue !== next.nodeValue) live.nodeValue = next.nodeValue;
    return;
  }
  morphAttrs(live, next, keep);
  morphChildren(live, next, keep);
}

export function morphChildren(live: MNode, next: MNode, keep?: KeepAttr): void {
  const liveKids = Array.from(live.childNodes);
  const keyed = new Map<string, MNode>();
  const loose: MNode[] = [];
  for (const kid of liveKids) {
    const key = keyOf(kid);
    if (key !== null) keyed.set(key, kid);
    else loose.push(kid);
  }

  const out: MNode[] = [];
  for (const want of Array.from(next.childNodes)) {
    const key = keyOf(want);
    let match: MNode | undefined;
    if (key !== null) {
      match = keyed.get(key);
      if (match && !sameKind(match, want)) match = undefined;
      if (match) keyed.delete(key);
    } else {
      const at = loose.findIndex((n) => sameKind(n, want));
      if (at >= 0) match = loose.splice(at, 1)[0];
    }
    if (match) {
      morphNode(match, want, keep);
      out.push(match);
    } else {
      out.push(want);
    }
  }

  out.forEach((node, i) => {
    const ref = live.childNodes[i] ?? null;
    if (ref !== node) live.insertBefore(node, ref);
  });
  while (live.childNodes.length > out.length) {
    live.removeChild(live.childNodes[live.childNodes.length - 1]);
  }
}
